import React, { useCallback, useMemo } from 'react';
import { Box, capitalizeFirstLetter, Hint, InfoOutlineIcon, Text, VerticalList } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ClientType, EMPTY_FIELD_VALUE } from '@/common/constants';
import regexp from '@/common/regexp';

import { EventType, LoginHistory, SourceTypeLabel, TRIGGER_ONLY_EVENTS } from './loginHistory.types';

const StyledInfoIcon = styled(InfoOutlineIcon)`
  &:hover {
    fill: ${({ theme }) => theme.colors.iconAccent};
  }
`;

const LoginHistoryListItemDetails: React.FC<Partial<LoginHistory>> = ({
  contactId,
  employeeIds,
  ipAddress,
  geolocation,
  userAgent,
  personalEmail,
  eventType,
  sourceType,
  mfaStatusType,
}) => {
  const isContactMode = useMemo(() => !!contactId, [contactId]);

  const joinedEmployeeIds = useMemo(() => {
    if (employeeIds?.length) {
      return employeeIds?.join(', ');
    }

    return null;
  }, [employeeIds]);

  const isSsoSource = useMemo(() => sourceType === ClientType.PARTNER_SSO, [sourceType]);

  const getItemDetails = useCallback(
    (value?: React.ReactNode | null) => {
      if (isSsoSource && !value) {
        return <Text color="textDisabled">Unavailable for SSO</Text>;
      }

      return value || EMPTY_FIELD_VALUE;
    },
    [isSsoSource]
  );

  const shouldShowSource = useMemo(() => !eventType || !TRIGGER_ONLY_EVENTS.includes(eventType), [eventType]);
  const shouldShowMfa = useMemo(() => shouldShowSource && eventType !== EventType.CHANGE_PASSWORD, [shouldShowSource, eventType]);

  return (
    <Box margin={{ top: 'spacing24' }} background="module" pad="spacing24" round="container1Round">
      <VerticalList data-testId="login-history-lint_item-details">
        <VerticalList.Item>
          <VerticalList.Item.Title width={{ max: '200px' }}>
            {isContactMode ? 'Contact ID' : 'Employee ID'}
          </VerticalList.Item.Title>
          <VerticalList.Item.Value>
            {getItemDetails(isContactMode ? contactId : joinedEmployeeIds)}
          </VerticalList.Item.Value>
        </VerticalList.Item>

        <VerticalList.Item>
          <VerticalList.Item.Title width={{ max: '200px' }}>IP address</VerticalList.Item.Title>
          <VerticalList.Item.Value>{getItemDetails(ipAddress)}</VerticalList.Item.Value>
        </VerticalList.Item>

        <VerticalList.Item>
          <VerticalList.Item.Title width={{ max: '200px' }}>Geolocation</VerticalList.Item.Title>
          <VerticalList.Item.Value>
            {getItemDetails(
              geolocation ? (
                <Box direction="row" align="center">
                  <Hint hintElement={<StyledInfoIcon color="textDisabled" size="20px" />} hintWidth={240}>
                    <Text textAlign="center" size="small">
                      Location is estimated and may not be accurate, especially with VPNs or mobile networks.
                    </Text>
                  </Hint>
                  <Text margin={{ left: 'spacing2' }} style={{ lineHeight: '20px' }}>
                    {geolocation}
                  </Text>
                </Box>
              ) : null
            )}
          </VerticalList.Item.Value>
        </VerticalList.Item>

        <VerticalList.Item>
          <VerticalList.Item.Title width={{ max: '200px' }}>User agent</VerticalList.Item.Title>
          <VerticalList.Item.Value
            textProps={{ style: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}
          >
            {getItemDetails(userAgent)}
          </VerticalList.Item.Value>
        </VerticalList.Item>

        {eventType === EventType.ACCOUNT_REGISTRATION && (
          <VerticalList.Item>
            <VerticalList.Item.Title width={{ max: '200px' }}>Personal email</VerticalList.Item.Title>
            <VerticalList.Item.Value>{getItemDetails(personalEmail)}</VerticalList.Item.Value>
          </VerticalList.Item>
        )}

        {shouldShowSource && (
          <VerticalList.Item>
            <VerticalList.Item.Title width={{ max: '200px' }}>Source</VerticalList.Item.Title>
            <VerticalList.Item.Value>
              {getItemDetails(sourceType ? SourceTypeLabel?.[sourceType] : null)}
            </VerticalList.Item.Value>
          </VerticalList.Item>
        )}

        {shouldShowMfa && (
          <VerticalList.Item>
            <VerticalList.Item.Title width={{ max: '200px' }}>
              Multi-Factor Authentication
            </VerticalList.Item.Title>
            <VerticalList.Item.Value>
              {getItemDetails(
                mfaStatusType
                  ? capitalizeFirstLetter(mfaStatusType.replaceAll(regexp.DASH_SYMBOL, ' '))
                  : null
              )}
            </VerticalList.Item.Value>
          </VerticalList.Item>
        )}
      </VerticalList>
    </Box>
  );
};

export default LoginHistoryListItemDetails;
